import axios from 'axios';
import { reportClientEvent } from '@/lib/clientDiagnostics';

const BACKEND_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
const API = `${BACKEND_URL}/api`;

// Intercepteur de REQUÊTE — injecte automatiquement le token Bearer sur chaque appel axios
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    if (token && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Retry automatique pour les erreurs réseau (backend en redémarrage)
// + 🔒 2026-05-01 : redirige vers /login si 401 "Session expirée" (force-logout global)
// + 🆕 2026-05-18 : normalise `detail` Pydantic 422 (array d'erreurs) vers string
//   pour éviter "Minified React error #31" quand toast.error(error.detail) reçoit un objet.
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    const status = error.response?.status;
    const detail = error.response?.data?.detail || '';

    // 🆕 [2A.3] Télescope : capture des 401/403/500 sur /api/* (warning ou error)
    try {
      const fullUrl = String(config?.url || '');
      const isApi = fullUrl.includes('/api/') && !fullUrl.includes('/api/diagnostics/');
      if (isApi && (status === 401 || status === 403 || (typeof status === 'number' && status >= 500))) {
        const respExcerpt = (() => {
          try {
            const d = error.response?.data;
            if (!d) return '';
            if (typeof d === 'string') return d.slice(0, 200);
            return JSON.stringify(d).slice(0, 200);
          } catch { return ''; }
        })();
        const path = (() => {
          try { return new URL(fullUrl, window.location.origin).pathname; }
          catch { return fullUrl.slice(0, 200); }
        })();
        reportClientEvent(
          'api_error',
          status >= 500 ? 'error' : (status === 403 ? 'warning' : 'error'),
          {
            status,
            url: path,
            method: (config?.method || 'GET').toUpperCase(),
            response_excerpt: respExcerpt,
          }
        );
      }
    } catch { /* silencieux */ }

    // 🆕 Normalisation Pydantic 422 : `detail` peut être un array d'objets
    // `[{type, loc, msg, input, url}]` qui crash si rendu comme enfant React.
    // On le transforme en string lisible : "champ.x: msg ; champ.y: msg".
    if (error.response?.data?.detail && typeof error.response.data.detail !== 'string') {
      const d = error.response.data.detail;
      try {
        if (Array.isArray(d)) {
          error.response.data.detail = d
            .map((e) => {
              const loc = Array.isArray(e?.loc) ? e.loc.filter(p => p !== 'body').join('.') : '';
              const msg = e?.msg || e?.message || JSON.stringify(e);
              return loc ? `${loc}: ${msg}` : msg;
            })
            .join(' ; ');
        } else if (typeof d === 'object') {
          error.response.data.detail = d.msg || d.message || JSON.stringify(d);
        }
      } catch (_) {
        error.response.data.detail = 'Erreur de validation';
      }
    }

    // 🔒 Session invalidée par force-logout-all → purger tokens + redirection
    if (status === 401 && (detail.includes('Session expirée') || detail.includes('reconnect'))) {
      try {
        localStorage.removeItem('auth_token');
        sessionStorage.removeItem('auth_token');
        localStorage.removeItem('admin_token');
        sessionStorage.removeItem('admin_token');
        localStorage.removeItem('impersonation_info');
        sessionStorage.removeItem('impersonation_info');
      } catch {}
      // Redirection douce (évite la boucle infinie si déjà sur /login)
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login?reason=session_expired';
      }
      return Promise.reject(error);
    }

    if (!config || config._retryCount >= 3) return Promise.reject(error);
    // Retry seulement sur les erreurs réseau ou 502/503/504
    const isNetworkError = !error.response || status === 502 || status === 503 || status === 504;
    if (!isNetworkError) return Promise.reject(error);
    config._retryCount = (config._retryCount || 0) + 1;
    const delay = config._retryCount * 1500; // 1.5s, 3s, 4.5s
    await new Promise(resolve => setTimeout(resolve, delay));
    return axios(config);
  }
);

export const api = {
  // Base URL for direct fetch calls
  baseUrl: API,

  // ─── Méthodes génériques HTTP (acceptent un path relatif ou absolu) ───
  // Préfixent automatiquement avec BACKEND_URL si le path commence par "/api"
  get: async (path, config) => {
    const url = path.startsWith('http') ? path : `${BACKEND_URL}${path}`;
    return axios.get(url, config);
  },
  post: async (path, data, config) => {
    const url = path.startsWith('http') ? path : `${BACKEND_URL}${path}`;
    return axios.post(url, data, config);
  },
  put: async (path, data, config) => {
    const url = path.startsWith('http') ? path : `${BACKEND_URL}${path}`;
    return axios.put(url, data, config);
  },
  patch: async (path, data, config) => {
    const url = path.startsWith('http') ? path : `${BACKEND_URL}${path}`;
    return axios.patch(url, data, config);
  },
  delete: async (path, config) => {
    const url = path.startsWith('http') ? path : `${BACKEND_URL}${path}`;
    return axios.delete(url, config);
  },

  // Dashboard - utilise les données réelles classifiées
  getDashboard: async (year, month) => {
    const response = await axios.get(`${API}/dashboard/real/${year}/${month}`);
    return response.data;
  },

  // Dashboard pour période (mensuel, trimestriel, annuel)
  getDashboardPeriod: async (view, year, month, quarter) => {
    const { getMonthsForPeriod, aggregateMonthlyData } = await import('./periodUtils');
    const periods = getMonthsForPeriod(view, year, month, quarter);
    
    // Récupérer les données pour chaque mois
    const monthsData = await Promise.all(
      periods.map(p => axios.get(`${API}/dashboard/real/${p.year}/${p.month}`).then(r => r.data))
    );
    
    // Agréger si nécessaire
    return aggregateMonthlyData(monthsData);
  },

  // Dashboard ancien (données simulées) - pour référence
  getDashboardSimulated: async (year, month) => {
    const response = await axios.get(`${API}/dashboard/${year}/${month}`);
    return response.data;
  },

  // Analyses
  getAnalyseMensuelle: async (year) => {
    const response = await axios.get(`${API}/analyse/mensuelle/${year}`);
    return response.data;
  },

  getAnalyseTrimestrielle: async (year) => {
    const response = await axios.get(`${API}/analyse/trimestrielle/${year}`);
    return response.data;
  },

  getAnalyseAnnuelle: async () => {
    const response = await axios.get(`${API}/analyse/annuelle`);
    return response.data;
  },

  // Cash Flow
  getCashflowBancaire: async (year, month) => {
    const response = await axios.get(`${API}/cashflow/bancaire/${year}/${month}`);
    return response.data;
  },

  // Argent Cash
  getCashSolde: async () => {
    const response = await axios.get(`${API}/cash/solde`);
    return response.data;
  },

  getCashHistorique: async (limit = 50) => {
    const response = await axios.get(`${API}/cash/historique?limit=${limit}`);
    return response.data;
  },

  createCashEntry: async (entry) => {
    const response = await axios.post(`${API}/cash/entry`, entry);
    return response.data;
  },

  // Transactions
  getTransactions: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await axios.get(`${API}/transactions?${queryString}`);
    return response.data;
  },

  createTransaction: async (transaction) => {
    const response = await axios.post(`${API}/transactions`, transaction);
    return response.data;
  },

  // Ratios
  getRatios: async (year, month) => {
    const response = await axios.get(`${API}/ratios/${year}/${month}`);
    return response.data;
  },

  // Objectifs
  getObjectifs: async (year) => {
    const response = await axios.get(`${API}/objectifs/${year}`);
    return response.data;
  },

  // Config
  getConfig: async () => {
    const response = await axios.get(`${API}/config`);
    return response.data;
  },

  // ============== GESTION DES EMPLOYÉS ==============
  
  getEmployees: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.statut) params.append('statut', filters.statut);
    if (filters.type_employe) params.append('type_employe', filters.type_employe);
    if (filters.departement) params.append('departement', filters.departement);
    if (filters.actif !== undefined) params.append('actif', filters.actif);
    // 🔧 2026-05-23 — include_inactive=true permet aux vues historiques
    // (Gestion Employés, stats dossiers, validation heures, etc.) de voir
    // aussi les employés inactifs. Par défaut, le backend filtre actifs only.
    if (filters.include_inactive) params.append('include_inactive', 'true');
    if (filters.include_owners) params.append('include_owners', 'true');
    const response = await axios.get(`${API}/employees?${params}`);
    return response.data;
  },

  getEmployee: async (employeeId) => {
    const response = await axios.get(`${API}/employees/${employeeId}`);
    return response.data;
  },

  createEmployee: async (employeeData) => {
    const response = await axios.post(`${API}/employees`, employeeData);
    return response.data;
  },

  updateEmployee: async (employeeId, updateData) => {
    const response = await axios.put(`${API}/employees/${employeeId}`, updateData);
    return response.data;
  },

  addEmployeeTraining: async (employeeId, trainingData) => {
    const response = await axios.post(`${API}/employees/${employeeId}/training`, trainingData);
    return response.data;
  },

  addEmployeeWarning: async (employeeId, warningData) => {
    const response = await axios.post(`${API}/employees/${employeeId}/warning`, warningData);
    return response.data;
  },

  updateEmployeeSalary: async (employeeId, salaryData, raison = "") => {
    const response = await axios.put(`${API}/employees/${employeeId}/salary?raison=${encodeURIComponent(raison)}`, salaryData);
    return response.data;
  },

  addContractSignature: async (employeeId, signatureData) => {
    const response = await axios.post(`${API}/employees/${employeeId}/contract-signature`, signatureData);
    return response.data;
  },

  getEmployeeWarnings: async (employeeId) => {
    const response = await axios.get(`${API}/employees/${employeeId}/warnings`);
    return response.data;
  },

  markWarningSeen: async (employeeId, warningId) => {
    const response = await axios.put(`${API}/employees/${employeeId}/warnings/${warningId}/seen`);
    return response.data;
  },

  // ============== SYSTÈME DE POINTAGE ==============

  punchIn: async (punchData) => {
    const response = await axios.post(`${API}/punch/in`, punchData);
    return response.data;
  },

  punchOut: async (punchData) => {
    const response = await axios.post(`${API}/punch/out`, punchData);
    return response.data;
  },

  getEmployeePunches: async (employeeId, dateFrom = null, dateTo = null) => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    const response = await axios.get(`${API}/punch/employee/${employeeId}?${params}`);
    return response.data;
  },

  getCurrentPunch: async (employeeId) => {
    const response = await axios.get(`${API}/punch/current/${employeeId}`);
    return response.data;
  },

  getAllPunches: async (date = null, statut = null) => {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (statut) params.append('statut', statut);
    const response = await axios.get(`${API}/punch/all?${params}`);
    return response.data;
  },

  validatePunch: async (punchId, valide = true, notes = null, validePar = "Manager") => {
    const params = new URLSearchParams();
    params.append('valide', valide);
    if (notes) params.append('notes', notes);
    params.append('valide_par', validePar);
    const response = await axios.put(`${API}/punch/${punchId}/validate?${params}`);
    return response.data;
  },

  // Horaires
  createSchedule: async (scheduleData) => {
    const response = await axios.post(`${API}/schedules`, scheduleData);
    return response.data;
  },

  getEmployeeSchedules: async (employeeId, dateFrom = null, dateTo = null) => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    const response = await axios.get(`${API}/schedules/employee/${employeeId}?${params}`);
    return response.data;
  },

  // Vérification des oublis de dépunch
  checkForgottenPunches: async () => {
    const response = await axios.post(`${API}/punch/check-forgotten`);
    return response.data;
  },

  // ============== CONTRATS ==============
  
  getContracts: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.actif !== undefined) params.append('actif', filters.actif);
    if (filters.season) params.append('season', filters.season);
    const response = await axios.get(`${API}/contracts?${params}`);
    return response.data;
  },

  // ============== IMPORT HISTORIQUE ==============
  
  importHeuresHistoriques: async (data) => {
    const response = await axios.post(`${API}/import/heures-historiques`, data);
    return response.data;
  },

  importMarketingHistorique: async (data) => {
    const response = await axios.post(`${API}/import/marketing-historique`, data);
    return response.data;
  },

  // Supprimer données fictives/de test
  deleteTestData: async () => {
    const response = await axios.delete(`${API}/admin/delete-test-data`);
    return response.data;
  }
};

export default api;
